import type { CalendarEvent } from "@/types/calendar";
import { getEventPosition, formatTimeShort } from "@/lib/calendar-utils";
import { cn } from "@/lib/utils";

interface EventBlockProps {
  event: CalendarEvent;
  color: string;
  isDraggingSelf?: boolean;
  isMovable?: boolean;
  onClick?: () => void;
  onMoveDragStart?: (event: CalendarEvent, e: React.PointerEvent<HTMLDivElement>) => void;
}

export function EventBlock({
  event,
  color,
  isDraggingSelf = false,
  isMovable = false,
  onClick,
  onMoveDragStart,
}: EventBlockProps) {
  const { top, height } = getEventPosition(event.start, event.end);
  const isShort = height < 40;

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!isMovable) return;
    if (e.pointerType !== "mouse") return;
    if (e.button !== 0) return;
    onMoveDragStart?.(event, e);
  };

  return (
    <div
      className="group absolute left-0.5 right-0.5 md:left-1 md:right-1"
      style={{
        top: `${top}px`,
        height: `${height}px`,
        opacity: isDraggingSelf ? 0.35 : 1,
        transition: "opacity 120ms ease-out",
      }}
      onPointerDown={handlePointerDown}
    >
      <div
        className={cn(
          "h-full rounded px-1 md:px-2 py-0.5 md:py-1 overflow-hidden",
          "flex flex-col",
          "text-[10px] md:text-sm",
          "border-l-2 md:border-l-4",
          "active:opacity-80 transition-opacity",
          isMovable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
        )}
        style={{
          backgroundColor: `${color}20`,
          borderLeftColor: color,
        }}
        onClick={onClick}
      >
        <div
          className={cn(
            "font-medium min-w-0",
            isShort ? "truncate text-[9px] md:text-xs" : "line-clamp-2",
          )}
          style={{ color }}
        >
          {event.summary}
        </div>
        {!isShort && (
          <div
            className="flex items-center gap-1 text-[8px] md:text-xs opacity-70 shrink-0 min-w-0"
            style={{ color }}
          >
            <span className="shrink-0">
              {formatTimeShort(event.start)} - {formatTimeShort(event.end)}
            </span>
            {event.organizer && (
              <>
                <span className="shrink-0">·</span>
                <span className="truncate max-w-[60px] md:max-w-[80px]">
                  {event.organizer.replace(/@.*$/, "")}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Hover Card - Desktop only */}
      {!isDraggingSelf && (
        <div className="hidden md:group-hover:block absolute left-0 top-full mt-1 z-30 pointer-events-none">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border p-3 min-w-[180px] max-w-[280px]">
            <div className="font-medium text-sm text-gray-900 dark:text-gray-100 mb-1">
              {event.summary}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
              {formatTimeShort(event.start)} - {formatTimeShort(event.end)}
            </div>
            {event.organizer && (
              <div className="text-xs text-gray-500 dark:text-gray-500">
                주최: {event.organizer.replace(/@.*$/, "")}
              </div>
            )}
            <div className="text-[10px] text-gray-400 mt-2">
              {isMovable ? "클릭: 상세 · 드래그: 이동" : "클릭하여 상세 보기"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
